import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import DiagnosticPDFExport from './components/DiagnosticPDFExport';

const DOMAINS = [
  {
    id: 'governance',
    title: 'Governance & Operating Model',
    icon: 'Building2',
    color: 'from-[#002B45] to-[#004070]',
    accent: '#002B45',
    objective: 'Assess structural alignment and governance execution',
    questions: [
      'Is there a formal data governance framework in place?',
      'Is there an active Data Governance Council?',
      'Are governance policies documented and enforced?',
      'Are governance processes integrated into day-to-day operations?',
      'Is governance seen as a business priority or IT initiative?',
    ],
  },
  {
    id: 'ownership',
    title: 'Data Ownership & Accountability',
    icon: 'UserCheck',
    color: 'from-[#1FB5A5] to-[#17a396]',
    accent: '#1FB5A5',
    objective: 'Evaluate clarity of ownership and responsibility',
    questions: [
      'Are data owners clearly defined for key domains?',
      'Do data owners actively take accountability for data quality?',
      'Are data stewards assigned and operational?',
      'Is there a clear RACI model in place?',
      'Are ownership responsibilities understood across the organisation?',
    ],
  },
  {
    id: 'quality',
    title: 'Data Quality Management',
    icon: 'ShieldCheck',
    color: 'from-[#0EA5E9] to-[#0284c7]',
    accent: '#0EA5E9',
    objective: 'Measure ability to ensure trusted data',
    questions: [
      'Are data quality dimensions defined (accuracy, completeness, etc.)?',
      'Are there automated data quality checks in pipelines?',
      'Are data quality issues tracked and resolved systematically?',
      'Is there a defined issue escalation process?',
      'Are data quality metrics measured and reported?',
    ],
  },
  {
    id: 'metadata',
    title: 'Metadata & Data Cataloging',
    icon: 'BookOpen',
    color: 'from-[#7C3AED] to-[#6d28d9]',
    accent: '#7C3AED',
    objective: 'Assess visibility and usability of data assets',
    questions: [
      'Is there a centralised data catalog?',
      'Are key data elements clearly defined in business terms?',
      'Is data lineage documented and accessible?',
      'Can users easily discover and understand data assets?',
      'Are ownership and stewardship visible in metadata tools?',
    ],
  },
  {
    id: 'security',
    title: 'Data Security & Compliance',
    icon: 'Lock',
    color: 'from-[#DC2626] to-[#b91c1c]',
    accent: '#DC2626',
    objective: 'Evaluate control and protection of data assets',
    questions: [
      'Is data classified based on sensitivity levels?',
      'Are access controls implemented using RBAC principles?',
      'Are access requests formally managed and audited?',
      'Are compliance requirements (e.g., GDPR) actively enforced?',
      'Is there visibility into who is accessing what data and why?',
    ],
  },
  {
    id: 'lifecycle',
    title: 'Data Lifecycle & Architecture Alignment',
    icon: 'GitBranch',
    color: 'from-[#D97706] to-[#b45309]',
    accent: '#D97706',
    objective: 'Assess governance integration across the data platform',
    questions: [
      'Is governance embedded across data ingestion, transformation, and consumption layers?',
      'Are there defined data contracts for source systems?',
      'Are staging, warehouse, and BI layers clearly structured and governed?',
      'Are schema changes controlled and communicated?',
      'Is there alignment between governance and modern architectures (e.g., Lakehouse, DW)?',
    ],
  },
];

const MATURITY_LEVELS = [
  { score: 1, label: 'Ad Hoc', description: 'No formal governance; reactive, inconsistent practices', color: 'bg-red-100 text-red-700 border-red-200' },
  { score: 2, label: 'Emerging', description: 'Some awareness; limited policies; inconsistent ownership', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { score: 3, label: 'Defined', description: 'Formal structures exist but not consistently enforced', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  { score: 4, label: 'Managed', description: 'Governance embedded in processes; measurable controls', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { score: 5, label: 'Optimised', description: 'Fully integrated, automated, continuously improved', color: 'bg-green-100 text-green-700 border-green-200' },
];

const OVERALL_MATURITY = [
  { min: 1.0, max: 1.9, label: 'Fragmented', description: 'High risk; no governance foundation', color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
  { min: 2.0, max: 2.9, label: 'Developing', description: 'Awareness exists but lacks structure', color: 'text-orange-600', bg: 'bg-orange-50 border-orange-200' },
  { min: 3.0, max: 3.9, label: 'Established', description: 'Solid foundation; needs consistency', color: 'text-yellow-600', bg: 'bg-yellow-50 border-yellow-200' },
  { min: 4.0, max: 4.5, label: 'Advanced', description: 'Strong governance embedded', color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
  { min: 4.6, max: 5.0, label: 'Leading', description: 'Optimised, scalable, best-in-class', color: 'text-green-600', bg: 'bg-green-50 border-green-200' },
];

const getMaturityLevel = (score) => {
  return MATURITY_LEVELS?.find(l => l?.score === Math.round(score)) || MATURITY_LEVELS?.[0];
};

const getOverallMaturity = (score) => {
  return OVERALL_MATURITY?.find(l => score >= l?.min && score <= l?.max) || OVERALL_MATURITY?.[0];
};

const getDomainMaturityLabel = (score) => {
  const rounded = Math.round(score);
  return MATURITY_LEVELS?.find(l => l?.score === rounded) || MATURITY_LEVELS?.[0];
};

const generateInsights = (domainScores) => {
  const sorted = [...domainScores]?.sort((a, b) => a?.avg - b?.avg);
  const lowest = sorted?.slice(0, 3);
  const highest = sorted?.slice(-2);

  const riskMap = {
    governance: 'Absence of a formal governance framework creates inconsistent decision-making and unmanaged data risk across the organisation.',
    ownership: 'Unclear data ownership leads to accountability gaps, unresolved data issues, and inconsistent reporting outcomes.',
    quality: 'Reactive data quality management results in unreliable analytics and high dependency on manual validation.',
    metadata: 'No centralised catalog or data definitions creates ambiguity, slows analyst onboarding, and reduces data trust.',
    security: 'Insufficient access controls and compliance enforcement expose the organisation to regulatory and reputational risk.',
    lifecycle: 'Lack of governance across the data platform leads to ungoverned pipelines, schema drift, and inconsistent data products.',
  };

  const quickWinMap = {
    governance: 'Establish a lightweight Data Governance Council with defined meeting cadence and decision rights.',
    ownership: 'Define data owners for Customer and Revenue domains — the two highest-impact areas to start.',
    quality: 'Implement basic automated data quality checks in existing ELT pipelines for critical data elements.',
    metadata: 'Introduce a lightweight data catalog (e.g., DataHub or Atlan) to document key business definitions.',
    security: 'Conduct a data classification exercise and implement RBAC for sensitive data domains.',
    lifecycle: 'Define data contracts for top 3 source systems and document the staging-to-warehouse layer.',
  };

  const roadmapMap = {
    governance: { phase: 'Days 1–30', action: 'Define governance charter, establish council, assign executive sponsor.' },
    ownership: { phase: 'Days 1–30', action: 'Map data domains, assign owners and stewards, publish RACI.' },
    quality: { phase: 'Days 31–60', action: 'Instrument quality rules in pipelines, build quality dashboard.' },
    metadata: { phase: 'Days 31–60', action: 'Deploy catalog tool, populate definitions for critical data elements.' },
    security: { phase: 'Days 61–90', action: 'Complete data classification, enforce RBAC, establish audit logging.' },
    lifecycle: { phase: 'Days 61–90', action: 'Document data contracts, govern schema change process, align with platform teams.' },
  };

  const criticalRisks = lowest?.map(d => ({ domain: d?.domain?.title, risk: riskMap?.[d?.domain?.id] }));
  const quickWins = lowest?.map(d => ({ domain: d?.domain?.title, win: quickWinMap?.[d?.domain?.id] }));
  const roadmap = domainScores?.map(d => ({
    domain: d?.domain?.title,
    score: d?.avg,
    ...roadmapMap?.[d?.domain?.id],
  }))?.sort((a, b) => a?.score - b?.score);

  return { criticalRisks, quickWins, roadmap };
};

const ScoreButton = ({ value, selected, onClick }) => {
  const labels = { 1: 'Ad Hoc', 2: 'Emerging', 3: 'Defined', 4: 'Managed', 5: 'Optimised' };
  const colors = {
    1: selected ? 'bg-red-600 text-white border-red-600' : 'border-red-200 text-red-600 hover:bg-red-50',
    2: selected ? 'bg-orange-500 text-white border-orange-500' : 'border-orange-200 text-orange-600 hover:bg-orange-50',
    3: selected ? 'bg-yellow-500 text-white border-yellow-500' : 'border-yellow-200 text-yellow-600 hover:bg-yellow-50',
    4: selected ? 'bg-blue-600 text-white border-blue-600' : 'border-blue-200 text-blue-600 hover:bg-blue-50',
    5: selected ? 'bg-green-600 text-white border-green-600' : 'border-green-200 text-green-600 hover:bg-green-50',
  };

  return (
    <button
      onClick={() => onClick(value)}
      className={`flex flex-col items-center justify-center w-full py-3 px-2 rounded-xl border-2 transition-all duration-200 font-semibold ${colors?.[value]}`}
    >
      <span className="text-xl font-bold">{value}</span>
      <span className="text-xs mt-0.5 font-medium hidden sm:block">{labels?.[value]}</span>
    </button>
  );
};

const RadarBar = ({ score, maxScore = 5, color }) => {
  const pct = (score / maxScore) * 100;
  return (
    <div className="w-full bg-gray-100 rounded-full h-2.5">
      <div
        className="h-2.5 rounded-full transition-all duration-700"
        style={{ width: `${pct}%`, backgroundColor: color }}
      />
    </div>
  );
};

const DataGovernanceDiagnostic = () => {
  const [phase, setPhase] = useState('intro'); // intro | assessment | results
  const [currentDomain, setCurrentDomain] = useState(0);
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState({});
  const [showPDFExport, setShowPDFExport] = useState(false);

  const totalQuestions = DOMAINS?.reduce((sum, d) => sum + d?.questions?.length, 0);
  const answeredQuestions = Object.keys(scores)?.length;
  const progressPct = Math.round((answeredQuestions / totalQuestions) * 100);

  const setScore = (domainId, qIdx, value) => {
    setScores(prev => ({ ...prev, [`${domainId}_${qIdx}`]: value }));
  };

  const getScore = (domainId, qIdx) => scores?.[`${domainId}_${qIdx}`] || null;

  const isDomainComplete = (domain) => {
    return domain?.questions?.every((_, i) => getScore(domain?.id, i) !== null);
  };

  const allComplete = DOMAINS?.every(isDomainComplete);

  const getDomainAvg = (domain) => {
    const vals = domain?.questions?.map((_, i) => getScore(domain?.id, i) || 0);
    return vals?.reduce((a, b) => a + b, 0) / vals?.length;
  };

  const overallScore = DOMAINS?.reduce((sum, d) => sum + getDomainAvg(d), 0) / DOMAINS?.length;

  const domainScores = DOMAINS?.map(d => ({ domain: d, avg: getDomainAvg(d) }));
  const insights = phase === 'results' ? generateInsights(domainScores) : null;
  const overallMaturity = getOverallMaturity(overallScore);

  const goToResults = () => {
    setPhase('results');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const restart = () => {
    setScores({});
    setNotes({});
    setCurrentDomain(0);
    setPhase('intro');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Data Governance Diagnostic Assessment | Northstar Analytics</title>
        <meta name="description" content="Assess your organisation's data governance maturity across 6 core domains. Get a prioritised roadmap, benchmark scores, and actionable insights." />
        <meta property="og:title" content="Data Governance Diagnostic Assessment | Northstar Analytics" />
        <meta property="og:description" content="Assess → Benchmark → Prioritise → Transform. A fast, high-impact diagnostic to understand where you are and what to fix first." />
        <meta property="og:type" content="website" />
      </Helmet>
      <Header />
      <main className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        {/* ─── INTRO ─── */}
        {phase === 'intro' && (
          <div className="max-w-4xl mx-auto">
            {/* Hero */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-secondary/10 text-secondary text-sm font-semibold px-4 py-2 rounded-full mb-6 uppercase tracking-wider">
                <Icon name="ClipboardCheck" size={16} />
                Free Diagnostic Tool
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4 leading-tight">
                Data Governance<br />
                <span className="text-secondary">Diagnostic Assessment</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-6">
                Assess → Benchmark → Prioritise → Transform
              </p>
              <p className="text-gray-500 max-w-2xl mx-auto">
                A structured, expert-designed diagnostic to evaluate your organisation's data governance maturity, identify gaps, and produce a clear prioritised roadmap for improvement.
              </p>
            </div>

            {/* What you get */}
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              {[
                { icon: 'BarChart3', title: 'Maturity Score', desc: 'Overall score and domain-level breakdown across 6 governance areas', color: 'bg-blue-50 text-blue-600' },
                { icon: 'AlertTriangle', title: 'Critical Risks', desc: 'Top 3 governance risks requiring immediate attention', color: 'bg-red-50 text-red-600' },
                { icon: 'Zap', title: 'Quick Wins', desc: 'Highest-impact, lowest-effort improvements to start this week', color: 'bg-amber-50 text-amber-600' },
                { icon: 'Map', title: '90-Day Roadmap', desc: 'Phased improvement plan with prioritised actions by domain', color: 'bg-green-50 text-green-600' },
              ]?.map((item) => (
                <div key={item?.title} className="flex items-start gap-4 bg-white rounded-2xl p-5 shadow-sm border border-border">
                  <div className={`p-3 rounded-xl ${item?.color} flex-shrink-0`}>
                    <Icon name={item?.icon} size={22} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-1">{item?.title}</h3>
                    <p className="text-sm text-gray-500">{item?.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Domains overview */}
            <div className="bg-white rounded-2xl border border-border p-6 mb-10 shadow-sm">
              <h2 className="text-lg font-bold text-primary mb-4">6 Core Governance Domains</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {DOMAINS?.map((d, i) => (
                  <div key={d?.id} className="flex items-center gap-3 p-3 rounded-xl bg-surface">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${d?.color} flex items-center justify-center flex-shrink-0`}>
                      <Icon name={d?.icon} size={16} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-primary">{d?.title}</p>
                      <p className="text-xs text-gray-400">{d?.questions?.length} questions</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Meta info */}
            <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500 mb-10">
              <span className="flex items-center gap-2"><Icon name="Clock" size={16} className="text-secondary" /> 15–20 minutes</span>
              <span className="flex items-center gap-2"><Icon name="ListChecks" size={16} className="text-secondary" /> 30 questions across 6 domains</span>
              <span className="flex items-center gap-2"><Icon name="CheckCircle2" size={16} className="text-secondary" /> Instant results, no sign-up required</span>
            </div>

            <div className="text-center">
              <Button
                size="lg"
                onClick={() => { setPhase('assessment'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="px-10 py-4 text-lg"
              >
                <Icon name="Play" size={20} className="mr-2" />
                Begin Diagnostic
              </Button>
            </div>
          </div>
        )}

        {/* ─── ASSESSMENT ─── */}
        {phase === 'assessment' && (
          <div className="max-w-3xl mx-auto">
            {/* Progress bar */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">{answeredQuestions} of {totalQuestions} questions answered</span>
                <span className="text-sm font-bold text-secondary">{progressPct}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div
                  className="h-2 rounded-full bg-secondary transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>

            {/* Domain tabs */}
            <div className="flex gap-2 overflow-x-auto pb-2 mb-8 scrollbar-hide">
              {DOMAINS?.map((d, i) => (
                <button
                  key={d?.id}
                  onClick={() => setCurrentDomain(i)}
                  className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 border ${
                    currentDomain === i
                      ? 'bg-primary text-white border-primary shadow-md': isDomainComplete(d)? 'bg-green-50 text-green-700 border-green-200': 'bg-white text-gray-600 border-border hover:border-primary/30'
                  }`}
                >
                  {isDomainComplete(d) && currentDomain !== i
                    ? <Icon name="CheckCircle2" size={14} className="text-green-600" />
                    : <Icon name={d?.icon} size={14} />
                  }
                  <span className="hidden sm:inline">{d?.title?.split(' ')?.slice(0, 2)?.join(' ')}</span>
                  <span className="sm:hidden">{i + 1}</span>
                </button>
              ))}
            </div>

            {/* Current domain */}
            {(() => {
              const domain = DOMAINS?.[currentDomain];
              return (
                <div>
                  {/* Domain header */}
                  <div className={`bg-gradient-to-r ${domain?.color} rounded-2xl p-6 text-white mb-6`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-white/20 rounded-lg">
                        <Icon name={domain?.icon} size={22} />
                      </div>
                      <div>
                        <p className="text-white/70 text-xs uppercase tracking-wider font-medium">Domain {currentDomain + 1} of {DOMAINS?.length}</p>
                        <h2 className="text-xl font-bold">{domain?.title}</h2>
                      </div>
                    </div>
                    <p className="text-white/80 text-sm">{domain?.objective}</p>
                  </div>
                  {/* Scoring guide */}
                  <div className="bg-white rounded-xl border border-border p-4 mb-6 shadow-sm">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Scoring Guide</p>
                    <div className="grid grid-cols-5 gap-2">
                      {MATURITY_LEVELS?.map(l => (
                        <div key={l?.score} className={`text-center p-2 rounded-lg border text-xs ${l?.color}`}>
                          <div className="font-bold text-base">{l?.score}</div>
                          <div className="font-semibold">{l?.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Questions */}
                  <div className="space-y-5">
                    {domain?.questions?.map((q, qIdx) => {
                      const selected = getScore(domain?.id, qIdx);
                      return (
                        <div key={qIdx} className={`bg-white rounded-2xl border-2 p-5 shadow-sm transition-all duration-200 ${selected ? 'border-secondary/40' : 'border-border'}`}>
                          <div className="flex items-start gap-3 mb-4">
                            <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${selected ? 'bg-secondary text-white' : 'bg-surface text-gray-400'}`}>
                              {qIdx + 1}
                            </span>
                            <p className="text-gray-800 font-medium leading-snug">{q}</p>
                          </div>
                          <div className="grid grid-cols-5 gap-2">
                            {[1, 2, 3, 4, 5]?.map(v => (
                              <ScoreButton
                                key={v}
                                value={v}
                                selected={selected === v}
                                onClick={(val) => setScore(domain?.id, qIdx, val)}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Notes */}
                  <div className="mt-6 bg-white rounded-2xl border border-border p-5 shadow-sm">
                    <label className="block text-sm font-semibold text-gray-600 mb-2">
                      <Icon name="MessageSquare" size={14} className="inline mr-1" />
                      Notes for this domain (optional)
                    </label>
                    <textarea
                      value={notes?.[domain?.id] || ''}
                      onChange={e => setNotes(prev => ({ ...prev, [domain?.id]: e?.target?.value }))}
                      placeholder="Add context, observations, or caveats for this domain..."
                      rows={3}
                      className="w-full text-sm border border-border rounded-xl p-3 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-secondary/30 resize-none"
                    />
                  </div>
                  {/* Navigation */}
                  <div className="flex items-center justify-between mt-6">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentDomain(Math.max(0, currentDomain - 1))}
                      disabled={currentDomain === 0}
                    >
                      <Icon name="ChevronLeft" size={16} className="mr-1" />
                      Previous
                    </Button>

                    {currentDomain < DOMAINS?.length - 1 ? (
                      <Button
                        onClick={() => { setCurrentDomain(currentDomain + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        disabled={!isDomainComplete(domain)}
                      >
                        Next Domain
                        <Icon name="ChevronRight" size={16} className="ml-1" />
                      </Button>
                    ) : (
                      <Button
                        onClick={goToResults}
                        disabled={!allComplete}
                        className="bg-secondary hover:bg-secondary/90"
                      >
                        <Icon name="BarChart3" size={16} className="mr-2" />
                        View Results
                      </Button>
                    )}
                  </div>
                  {!isDomainComplete(domain) && (
                    <p className="text-center text-xs text-gray-400 mt-3">Score all {domain?.questions?.length} questions to continue</p>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* ─── RESULTS ─── */}
        {phase === 'results' && insights && (
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-secondary/10 text-secondary text-sm font-semibold px-4 py-2 rounded-full mb-4 uppercase tracking-wider">
                <Icon name="FileBarChart" size={16} />
                Northstar Diagnostic Summary
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-primary mb-2">Your Governance Maturity Report</h1>
              <p className="text-gray-500">Generated by Northstar Analytics · {new Date()?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>

            {/* Overall Score Card */}
            <div className={`rounded-2xl border-2 p-8 mb-8 ${overallMaturity?.bg}`}>
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="text-center md:text-left">
                  <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-1">Overall Maturity Score</p>
                  <div className="flex items-end gap-3">
                    <span className={`text-7xl font-bold ${overallMaturity?.color}`}>{overallScore?.toFixed(1)}</span>
                    <span className="text-gray-400 text-2xl mb-3">/ 5.0</span>
                  </div>
                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold text-lg ${overallMaturity?.color} bg-white/60 border border-current/20`}>
                    {overallMaturity?.label}
                  </div>
                </div>
                <div className="flex-1 md:border-l md:border-current/20 md:pl-6">
                  <p className="text-gray-600 font-medium mb-3">{overallMaturity?.description}</p>
                  <div className="space-y-1">
                    {OVERALL_MATURITY?.map(m => (
                      <div key={m?.label} className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${overallScore >= m?.min && overallScore <= m?.max ? 'bg-current scale-150' : 'bg-gray-300'}`} style={{ color: m?.color?.replace('text-', '') }} />
                        <span className={`text-sm ${overallScore >= m?.min && overallScore <= m?.max ? `font-bold ${m?.color}` : 'text-gray-400'}`}>
                          {m?.min?.toFixed(1)}–{m?.max?.toFixed(1)} · {m?.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Domain Breakdown */}
            <div className="bg-white rounded-2xl border border-border p-6 mb-8 shadow-sm">
              <h2 className="text-xl font-bold text-primary mb-6 flex items-center gap-2">
                <Icon name="LayoutGrid" size={20} className="text-secondary" />
                Domain-Level Breakdown
              </h2>
              <div className="space-y-5">
                {domainScores?.map(({ domain, avg }) => {
                  const maturity = getDomainMaturityLabel(avg);
                  return (
                    <div key={domain?.id}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${domain?.color} flex items-center justify-center`}>
                            <Icon name={domain?.icon} size={14} className="text-white" />
                          </div>
                          <span className="font-semibold text-gray-800 text-sm">{domain?.title}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${maturity?.color}`}>{maturity?.label}</span>
                          <span className="font-bold text-primary w-8 text-right">{avg?.toFixed(1)}</span>
                        </div>
                      </div>
                      <RadarBar score={avg} color={domain?.accent} />
                      {notes?.[domain?.id] && (
                        <p className="text-xs text-gray-400 mt-1 italic">Note: {notes?.[domain?.id]}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Risks + Quick Wins */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Critical Risks */}
              <div className="bg-white rounded-2xl border border-red-100 p-6 shadow-sm">
                <h2 className="text-lg font-bold text-red-700 mb-4 flex items-center gap-2">
                  <Icon name="AlertTriangle" size={18} />
                  Top 3 Critical Risks
                </h2>
                <div className="space-y-4">
                  {insights?.criticalRisks?.map((r, i) => (
                    <div key={i} className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 text-red-600 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                      <div>
                        <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-0.5">{r?.domain}</p>
                        <p className="text-sm text-gray-600">{r?.risk}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Wins */}
              <div className="bg-white rounded-2xl border border-green-100 p-6 shadow-sm">
                <h2 className="text-lg font-bold text-green-700 mb-4 flex items-center gap-2">
                  <Icon name="Zap" size={18} />
                  Top 3 Quick Wins
                </h2>
                <div className="space-y-4">
                  {insights?.quickWins?.map((w, i) => (
                    <div key={i} className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-600 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                      <div>
                        <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-0.5">{w?.domain}</p>
                        <p className="text-sm text-gray-600">{w?.win}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 90-Day Roadmap */}
            <div className="bg-white rounded-2xl border border-border p-6 mb-8 shadow-sm">
              <h2 className="text-xl font-bold text-primary mb-2 flex items-center gap-2">
                <Icon name="Map" size={20} className="text-secondary" />
                90-Day Improvement Roadmap
              </h2>
              <p className="text-sm text-gray-500 mb-6">Prioritised by lowest maturity score — address foundational gaps first.</p>

              {/* Phase buckets */}
              {[
                { label: 'Days 1–30', subtitle: 'Foundation', icon: 'Layers', color: 'bg-red-50 border-red-200 text-red-700' },
                { label: 'Days 31–60', subtitle: 'Structure', icon: 'GitBranch', color: 'bg-amber-50 border-amber-200 text-amber-700' },
                { label: 'Days 61–90', subtitle: 'Embed & Scale', icon: 'TrendingUp', color: 'bg-green-50 border-green-200 text-green-700' },
              ]?.map(phase => {
                const items = insights?.roadmap?.filter(r => r?.phase === phase?.label);
                if (!items?.length) return null;
                return (
                  <div key={phase?.label} className={`rounded-xl border p-4 mb-4 ${phase?.color}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Icon name={phase?.icon} size={16} />
                      <span className="font-bold text-sm">{phase?.label}</span>
                      <span className="text-xs opacity-70">· {phase?.subtitle}</span>
                    </div>
                    <div className="space-y-3">
                      {items?.map((item, i) => (
                        <div key={i} className="flex gap-3 bg-white/60 rounded-lg p-3">
                          <Icon name="ArrowRight" size={14} className="flex-shrink-0 mt-0.5 opacity-60" />
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide opacity-70 mb-0.5">{item?.domain}</p>
                            <p className="text-sm font-medium">{item?.action}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Scores table */}
            <div className="bg-white rounded-2xl border border-border p-6 mb-8 shadow-sm overflow-x-auto">
              <h2 className="text-lg font-bold text-primary mb-4 flex items-center gap-2">
                <Icon name="Table" size={18} className="text-secondary" />
                Scoring Summary
              </h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 pr-4 text-gray-500 font-semibold">Domain</th>
                    <th className="text-center py-2 px-3 text-gray-500 font-semibold">Score</th>
                    <th className="text-left py-2 pl-3 text-gray-500 font-semibold">Maturity Level</th>
                  </tr>
                </thead>
                <tbody>
                  {domainScores?.map(({ domain, avg }) => {
                    const m = getDomainMaturityLabel(avg);
                    return (
                      <tr key={domain?.id} className="border-b border-border/50 hover:bg-surface/50 transition-colors">
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 rounded bg-gradient-to-br ${domain?.color} flex items-center justify-center`}>
                              <Icon name={domain?.icon} size={12} className="text-white" />
                            </div>
                            <span className="font-medium text-gray-800">{domain?.title}</span>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className="font-bold text-primary text-base">{avg?.toFixed(1)}</span>
                        </td>
                        <td className="py-3 pl-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${m?.color}`}>{m?.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="bg-primary/5">
                    <td className="py-3 pr-4 font-bold text-primary">Overall Score</td>
                    <td className="py-3 px-3 text-center font-bold text-primary text-lg">{overallScore?.toFixed(1)}</td>
                    <td className="py-3 pl-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${overallMaturity?.bg} ${overallMaturity?.color}`}>{overallMaturity?.label}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-br from-primary to-[#004070] rounded-2xl p-8 text-white text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 text-xs font-semibold px-3 py-1.5 rounded-full mb-4 uppercase tracking-wider">
                <Icon name="Compass" size={14} />
                Northstar Analytics
              </div>
              <h2 className="text-2xl font-bold mb-3">Ready to Act on These Findings?</h2>
              <p className="text-white/80 max-w-xl mx-auto mb-6">
                This diagnostic is the starting point. Our consultants can facilitate a 1–2 hour stakeholder workshop to validate findings, align priorities, and build your governance transformation roadmap.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/schedule-consultation">
                  <Button className="bg-secondary hover:bg-secondary/90 text-white px-6">
                    <Icon name="Calendar" size={16} className="mr-2" />
                    Schedule a Free Consultation
                  </Button>
                </Link>
                <Link to="/contact-multi-channel-engagement-hub">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-6">
                    <Icon name="FileText" size={16} className="mr-2" />
                    Request a Proposal
                  </Button>
                </Link>
              </div>
            </div>

            {/* PDF Export */}
            <div className="bg-white rounded-2xl border border-border p-6 mb-8 shadow-sm text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-secondary/10 mb-4">
                <Icon name="FileDown" size={24} className="text-secondary" />
              </div>
              <h3 className="text-lg font-bold text-primary mb-2">Download Your Diagnostic Report</h3>
              <p className="text-sm text-gray-500 max-w-md mx-auto mb-5">
                Get a professionally branded Northstar PDF report with your full results — maturity score, domain breakdown, critical risks, quick wins, and 90-day roadmap. Great for sharing with stakeholders.
              </p>
              <Button
                onClick={() => setShowPDFExport(true)}
                className="bg-secondary hover:bg-secondary/90 text-white px-8"
              >
                <Icon name="Download" size={16} className="mr-2" />
                Download PDF Report
              </Button>
            </div>

            {/* Restart */}
            <div className="text-center">
              <button
                onClick={restart}
                className="text-sm text-gray-400 hover:text-primary transition-colors flex items-center gap-1.5 mx-auto"
              >
                <Icon name="RotateCcw" size={14} />
                Retake Assessment
              </button>
            </div>

            {/* PDF Export Modal */}
            {showPDFExport && (
              <DiagnosticPDFExport
                overallScore={overallScore}
                overallMaturity={overallMaturity}
                domainScores={domainScores}
                insights={insights}
                onClose={() => setShowPDFExport(false)}
              />
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default DataGovernanceDiagnostic;
